import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SparklesIcon } from '@heroicons/react/24/outline';
import ConnectOwnApp, { OwnAppWebhook } from './ConnectOwnApp';
import ChannelTrialNotice from '../common/ChannelTrialNotice';
import {
  connectWhatsAppNumber,
  disconnectWhatsAppNumber,
  getWhatsAppNumber,
  updateWhatsAppNumber,
} from '../../services/tenant.service';
import { loadFacebookSdk, readSignupMessage } from '../../utils/facebookSdk';

// Meta posts the chosen number to the window around the time FB.login calls
// back; the code it hands us is only valid for 30 seconds, so wait at most 5.
const SESSION_POLL_MS = 200;
const SESSION_POLLS = 25;

function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

function ConnectButtons({ busy, onConnect }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-lg border border-gray-200 p-3">
        <button
          type="button"
          onClick={() => onConnect('cloud_api')}
          disabled={busy}
          className="w-full rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
        >
          Connect a new number
        </button>
        <p className="mt-2 text-xs text-gray-500">
          A number that isn&rsquo;t used in the WhatsApp app. Your team reads and replies to chats here in Serviti.
        </p>
      </div>
      <div className="rounded-lg border border-gray-200 p-3">
        <button
          type="button"
          onClick={() => onConnect('business_app')}
          disabled={busy}
          className="w-full rounded-md border border-green-600 px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
        >
          Use my WhatsApp Business app number
        </button>
        <p className="mt-2 text-xs text-gray-500">
          Keep using the WhatsApp Business app on your phone. The AI answers alongside it and steps back when
          you reply.
        </p>
      </div>
    </div>
  );
}

export default function WhatsAppAgentCard({ tenantId, isSuperadmin = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [choosingConnection, setChoosingConnection] = useState(false);
  const sessionRef = useRef(null);
  const facebookRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setState(await getWhatsAppNumber(tenantId));
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  // Load Meta's SDK as soon as a connect button is on screen. The click must
  // open Meta's window straight away: browsers block a popup that opens after
  // waiting for a download.
  const appId = state?.signup?.app_id;
  const graphVersion = state?.signup?.graph_version;
  const showsConnect = Boolean(
    state?.entitled && appId && (!state?.connection || state.connection.status !== 'connected'),
  );
  useEffect(() => {
    if (!showsConnect) return undefined;
    let live = true;
    loadFacebookSdk(appId, graphVersion)
      .then((FB) => {
        if (live) facebookRef.current = FB;
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [showsConnect, appId, graphVersion]);

  useEffect(() => {
    const onMessage = (event) => {
      const message = readSignupMessage(event);
      if (message) sessionRef.current = message;
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const waitForSession = async () => {
    for (let poll = 0; poll < SESSION_POLLS && !sessionRef.current; poll += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, SESSION_POLL_MS));
    }
    return sessionRef.current;
  };

  const finish = async (code, onboarding) => {
    if (!code) {
      if (sessionRef.current?.event === 'CANCEL') toast('WhatsApp connection cancelled.');
      else toast.error('Meta didn’t finish connecting a number. Please try again.');
      return;
    }
    const session = await waitForSession();
    const ids = session?.data || {};
    if (!session || !session.event.startsWith('FINISH') || !ids.phone_number_id || !ids.waba_id) {
      toast.error('Pick a WhatsApp number in the Meta window to connect it.');
      return;
    }
    try {
      const next = await connectWhatsAppNumber(tenantId, {
        code,
        waba_id: String(ids.waba_id),
        phone_number_id: String(ids.phone_number_id),
        business_id: ids.business_id ? String(ids.business_id) : null,
        onboarding,
      });
      setState(next);
      if (next?.connection?.status === 'connected') {
        toast.success('WhatsApp connected. Your agent answers there now.');
      } else {
        toast.error('Connected, but Meta reported a problem. See below.');
      }
    } catch (err) {
      toast.error(errorText(err, 'Could not connect WhatsApp. Please try again.'));
    }
  };

  const connect = async (onboarding) => {
    const signup = state?.signup;
    if (!signup) return;
    setBusy('connect');
    sessionRef.current = null;
    try {
      // Already loaded, so FB.login runs inside the click (see above).
      const FB = facebookRef.current || await loadFacebookSdk(signup.app_id, signup.graph_version);
      await new Promise((resolve) => {
        // FB.login refuses async callbacks, so this one stays plain.
        FB.login(
          (response) => {
            finish(response?.authResponse?.code, onboarding).finally(resolve);
          },
          {
            config_id: signup.config_id,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              setup: {},
              sessionInfoVersion: '3',
              ...(onboarding === 'business_app' ? { featureType: 'whatsapp_business_app_onboarding' } : {}),
            },
          },
        );
      });
    } catch (err) {
      toast.error(err?.message || 'Could not open Facebook sign-in.');
    } finally {
      setBusy('');
    }
  };

  const save = async (changes, message) => {
    setBusy('save');
    try {
      setState(await updateWhatsAppNumber(tenantId, changes));
      toast.success(message);
    } catch (err) {
      toast.error(errorText(err, 'Could not save. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      setState(await disconnectWhatsAppNumber(tenantId));
      setConfirmingDisconnect(false);
      toast.success('WhatsApp disconnected. Past chats stay in your inbox.');
    } catch (err) {
      toast.error(errorText(err, 'Could not disconnect. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  if (loading) return <p role="status" className="p-4 text-sm">Loading WhatsApp connection…</p>;
  if (!state) return <div role="alert" className="p-4 text-sm">
    <p>Could not load your WhatsApp connection.</p>
    <button type="button" onClick={load} className="mt-2 min-h-[44px] font-semibold text-indigo-700">Retry connection</button>
  </div>;
  const { entitled, connection, agents = [] } = state;

  return (
    <section className="bg-white rounded-lg border border-gray-200 shadow-sm" aria-labelledby="wa-agent-title">
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-green-600" />
          <h2 id="wa-agent-title" className="text-lg font-semibold text-gray-900">AI agent on your WhatsApp</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Your AI agent answers customers on your own WhatsApp Business number. Your team can take over any chat.
        </p>
      </div>

      <div className="px-5 py-4 space-y-4">
        <ChannelTrialNotice until={state.trial_until} tenantId={tenantId} />
        {!entitled && (
          <p className="text-sm text-gray-700">
            {connection
              ? 'The AI agent on WhatsApp add-on isn’t active on this workspace, so the AI isn’t answering.'
              : state.plan_allows === false
                ? 'Available on Growth and above only.'
                : 'This is an add-on.'}{' '}
            {isSuperadmin ? (
              'Grant it as a feature override on the tenant, or the customer buys it on their Billing page.'
            ) : (
              <Link to={`/t/${tenantId}/billing`} className="font-medium text-indigo-600 hover:underline">
                {state.plan_allows === false ? 'Upgrade on the Billing page' : 'Add it on the Billing page'}
              </Link>
            )}
          </p>
        )}

        {entitled && !connection && (
          <>
            {!choosingConnection && <button type="button" onClick={() => setChoosingConnection(true)}
              className="min-h-[44px] rounded-lg bg-green-700 px-4 py-2 font-semibold text-white">Connect WhatsApp</button>}
            {choosingConnection && <>
            <p className="text-sm text-gray-700">
              Sign in with the Facebook account that manages your business. Meta will ask you to pick or add a
              number and confirm it with a code.
            </p>
            <ConnectButtons busy={Boolean(busy)} onConnect={connect} />
            {/* For a business that will not hand its WhatsApp account to a
                provider, and the only path until Serviti is an approved Tech
                Provider. */}
            <ConnectOwnApp tenantId={tenantId} onConnected={setState} />
            </>}
          </>
        )}

        {connection && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-base font-semibold text-gray-900">
                  {connection.display_phone_number || connection.phone_number_id}
                </p>
                {connection.verified_name && <p className="text-sm text-gray-500">{connection.verified_name}</p>}
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  connection.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {connection.status === 'connected' ? 'Connected' : 'Needs attention'}
              </span>
            </div>

            {connection.onboarding === 'own_app' && connection.status === 'connected' && (
              <OwnAppWebhook url={connection.webhook_url} verifyToken={connection.webhook_verify_token} />
            )}

            {connection.status !== 'connected' && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 space-y-3">
                <p className="text-sm text-red-900">
                  {connection.last_error || 'Setup didn\u2019t finish. Please connect again.'}
                </p>
                {connection.onboarding !== 'own_app' && <ConnectButtons busy={Boolean(busy)} onConnect={connect} />}
              </div>
            )}

            {entitled && connection.onboarding === 'own_app' && (
              <ConnectOwnApp key={connection.id} tenantId={tenantId} connection={connection} onConnected={setState} />
            )}

            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={connection.ai_enabled}
                disabled={Boolean(busy)}
                onChange={(e) => save(
                  { ai_enabled: e.target.checked },
                  e.target.checked ? 'The AI answers new WhatsApp messages.' : 'The AI stopped answering on WhatsApp.',
                )}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              AI answers new messages
            </label>

            {agents.length > 0 && (
              <div>
                <label htmlFor="wa-agent-select" className="block text-sm font-medium text-gray-700">
                  Agent that answers
                </label>
                <select
                  id="wa-agent-select"
                  value={connection.agent_id || ''}
                  disabled={Boolean(busy)}
                  onChange={(e) => save({ agent_id: e.target.value }, 'Agent updated.')}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:ring-green-500"
                >
                  {!connection.agent_id && <option value="">Choose an agent</option>}
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
            )}

            <p className="text-sm text-gray-600">
              Meta needs a payment method on your WhatsApp account before your number can send messages.{' '}
              <a
                href={connection.whatsapp_manager_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:underline"
              >
                Open WhatsApp Manager
              </a>
            </p>
            <p className="text-sm text-gray-600">
              Chats appear in your{' '}
              <Link to={`/t/${tenantId}/whatsapp-inbox`} className="font-medium text-indigo-600 hover:underline">
                Customer inbox
              </Link>
              , where your team can take over or reply.
            </p>

            {confirmingDisconnect ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm text-gray-800">
                  Disconnect {connection.display_phone_number || 'this number'}? The AI stops answering there.
                  Past chats stay in your inbox.
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={disconnect}
                    disabled={Boolean(busy)}
                    className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDisconnect(false)}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white"
                  >
                    Keep it
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDisconnect(true)}
                disabled={Boolean(busy)}
                className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
              >
                Disconnect this number
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
